/**
 * PinnedPatientsSection — Renders pinned patients using PatientRow inside DashboardSection.
 */
import React, { useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { setPatientPinPreference } from "../../utils/patientDirectoryService";
import { useAuth } from "../../utils/authContext";
import DashboardSection from "./DashboardSection";
import PatientRow from "./PatientRow";
import { sortDashboardPatients } from "./dashboardSortUtil";

const SECTION_TITLE = "Pinned Patients";

const PinnedPatientsSection = ({ patients, dashboardPrefs }) => {
    const navigate = useNavigate();
    const { currentUser } = useAuth();
    const { getSectionPrefs, setSectionSort, setSectionCollapsed } = dashboardPrefs;
    const sectionPrefs = getSectionPrefs(SECTION_TITLE);

    const sortedPatients = useMemo(
        () => sortDashboardPatients(patients, sectionPrefs.sortField, sectionPrefs.sortDirection),
        [patients, sectionPrefs.sortField, sectionPrefs.sortDirection]
    );

    const handleNavigate = useCallback((id) => {
        navigate("/patient", { state: { patientId: id, from: "/dashboard" } });
    }, [navigate]);

    const handleRecord = useCallback((id) => {
        navigate("/patient", { state: { patientId: id, pendingAction: "record", from: "/dashboard" } });
    }, [navigate]);

    const handlePinToggle = useCallback(async (patient) => {
        if (patient.isShared || !currentUser?.uid) return;
        try {
            await setPatientPinPreference(currentUser.uid, patient.id, !patient.pin);
        } catch (err) {
            console.error("[PinnedPatientsSection] Error toggling pin:", err);
        }
    }, [currentUser?.uid]);

    const handleSort = useCallback((field, direction) => {
        setSectionSort(SECTION_TITLE, field, direction);
    }, [setSectionSort]);

    const handleToggleCollapse = useCallback((collapsed) => {
        setSectionCollapsed(SECTION_TITLE, collapsed);
    }, [setSectionCollapsed]);

    if (patients.length === 0) {
        return (
            <DashboardSection title={SECTION_TITLE} count={0} collapsed={sectionPrefs.collapsed} onToggleCollapse={handleToggleCollapse}>
                <p className="cdv-section-hint">
                    Pin patients from the Patients page to see them here.
                </p>
            </DashboardSection>
        );
    }

    return (
        <DashboardSection
            title={SECTION_TITLE}
            count={patients.length}
            collapsed={sectionPrefs.collapsed}
            onToggleCollapse={handleToggleCollapse}
            sortField={sectionPrefs.sortField}
            sortDirection={sectionPrefs.sortDirection}
            onSort={handleSort}
            columnHeader={true}
        >
            {sortedPatients.map((patient) => (
                <PatientRow
                    key={patient.id}
                    patient={patient}
                    onNavigate={handleNavigate}
                    onRecord={handleRecord}
                    showPin={!patient.isShared}
                    onPinToggle={handlePinToggle}
                />
            ))}
        </DashboardSection>
    );
};

export default PinnedPatientsSection;
